import {
  type FPlusCaretPosition,
  type FPlusTemplateOperationContext,
  isParameterDefinitionCreateOperation,
  isTemplateCreateOperation,
  type ParameterDefinitionCreateOperation,
  type TemplateCreateOperation,
} from '@festina/configuration-general-types/configuration'
import type { FPlusOperationContext } from '@festina/legalentity-general-types/legalentity'
import type { FPlusParameterDefinitionOperationContext } from '@festina/life-general-types/life'
import { STANDARD_BRANCH } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import type { LanguageServerInput } from '@festina-life/fplus'
import { useCallback } from 'react'

import { useFPlusTypeCheckingContext } from '../../context/fPlusTypeCheckingContext'
import { useNavigationParams } from '../../system/useNavigationParams'

export type ConfigurationLanguageServerInputProviderProps = {
  expressionPath?: Optional<string>
  expressions?: Optional<string | string[]>
  caret?: Optional<FPlusCaretPosition>
  prefixFunctionDeclaration?: Optional<string>
}
export type useConfigurationLanguageServerInputInitializerProps = (
  props: ConfigurationLanguageServerInputProviderProps,
) => LanguageServerInput

function createTemplateOperationContext(
  templateOperation: TemplateCreateOperation,
  expressionPath: Optional<string>,
): FPlusTemplateOperationContext {
  return {
    templateOperation,
    expressionPath,
    _type: 'FPlusTemplateOperationContext',
  }
}

function createParameterDefinitionOperationContext(
  parameterDefinitionOperation: ParameterDefinitionCreateOperation,
  expressionPath: Optional<string>,
): FPlusParameterDefinitionOperationContext {
  return {
    operation: parameterDefinitionOperation,
    dimensionName: expressionPath,
    _type: 'FPlusParameterDefinitionOperationContext',
  }
}

export const useConfigurationLanguageServerInputInitializer =
  (): useConfigurationLanguageServerInputInitializerProps => {
    const { context, currentOperation } = useFPlusTypeCheckingContext()
    const { server, branch } = useNavigationParams()

    return useCallback(
      ({ expressionPath, expressions, caret, prefixFunctionDeclaration }) => {
        let operationContext: Optional<FPlusOperationContext> = null

        if (currentOperation) {
          if (isTemplateCreateOperation(currentOperation)) {
            operationContext = createTemplateOperationContext(
              currentOperation,
              expressionPath,
            )
          } else if (isParameterDefinitionCreateOperation(currentOperation)) {
            operationContext = createParameterDefinitionOperationContext(
              currentOperation,
              expressionPath,
            )
          }
        }

        return {
          context: {
            ...context,
            operationContext,
          },
          server: branch === STANDARD_BRANCH ? server : null,
          expressions,
          caret,
          providerOptions: {
            hoverDisabled: false,
          },
          prefixFunctionDeclaration,
        }
      },
      [currentOperation, context, branch, server],
    )
  }
